import { createSlice } from '@reduxjs/toolkit'
import {
  getSngLobbyThunk,
  registerSngThunk,
  unregisterSngThunk,
  getSngLeaderboardThunk,
  getSngMyStatsThunk,
  getSngStandingsThunk
} from 'redux-thunk/thunk/sng.thunk'

const REGISTERED_IDS_KEY = 'sng_registered_tournament_ids'

const loadRegisteredIds = () => {
  try {
    const raw = sessionStorage.getItem(REGISTERED_IDS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

const persistRegisteredIds = (ids) => {
  try {
    sessionStorage.setItem(REGISTERED_IDS_KEY, JSON.stringify(ids.map(String)))
  } catch {
    /* ignore */
  }
}

const initialState = {
  tournaments: [],
  registeredTournamentIds: loadRegisteredIds(),
  leaderboard: [],
  leaderboardCount: 0,
  myStats: null,
  standings: [],
  standingsMeta: null,
  blindLevel: null,
  loading: false,
  actionLoading: false,
  error: null
}

const sngSlice = createSlice({
  name: 'sng',
  initialState,
  reducers: {
    applySngLobbyUpdate: (state, action) => {
      const data = action.payload || {}
      const tournamentId = String(data.tournamentId || '')
      if (!tournamentId) return

      const idx = state.tournaments.findIndex((t) => String(t.id) === tournamentId)
      if (idx === -1) {
        // New tournament spawned after previous completed — refetch will fill details;
        // insert a lightweight stub so UI can refresh counts.
        if (data.status === 'REGISTERING' || data.status === 'OPEN' || data.status === 'SCHEDULED') {
          state.tournaments.push({
            id: tournamentId,
            status: data.status,
            registrationCount: data.registrationCount ?? 0,
            maxPlayers: data.maxPlayers ?? 0,
            prizePool: data.prizePool ?? 0,
            tournamentTableId: data.tournamentTableId || null,
            tableStatus: data.tableStatus || null,
            countdownEndsAt: data.countdownEndsAt || null
          })
        }
        return
      }

      const row = state.tournaments[idx]
      if (data.registrationCount != null) row.registrationCount = data.registrationCount
      if (data.queueCount != null) row.queueCount = data.queueCount
      if (data.maxPlayers != null) row.maxPlayers = data.maxPlayers
      if (data.prizePool != null) row.prizePool = data.prizePool
      if (data.status != null) row.status = data.status
      if (data.tableStatus != null) row.tableStatus = data.tableStatus
      if (data.tournamentTableId != null) row.tournamentTableId = data.tournamentTableId
      if (data.countdownEndsAt != null) row.countdownEndsAt = data.countdownEndsAt

      // Keep tournament in lobby while window is open so more players can queue
      // in parallel with running tables.
      if (data.status === 'CANCELLED' || data.status === 'CLOSED' || data.status === 'COMPLETED') {
        state.tournaments.splice(idx, 1)
        state.registeredTournamentIds = state.registeredTournamentIds.filter((id) => id !== tournamentId)
        persistRegisteredIds(state.registeredTournamentIds)
      }
    },
    markRegistered: (state, action) => {
      const id = String(action.payload)
      if (!state.registeredTournamentIds.includes(id)) {
        state.registeredTournamentIds.push(id)
        persistRegisteredIds(state.registeredTournamentIds)
      }
    },
    markUnregistered: (state, action) => {
      const id = String(action.payload)
      state.registeredTournamentIds = state.registeredTournamentIds.filter((x) => x !== id)
      persistRegisteredIds(state.registeredTournamentIds)
    },
    // Bust-out: drop the seat locally so the lobby's auto-rejoin effect does not
    // send the player back to a table they were just eliminated from.
    markMyRegistrationEliminated: (state, action) => {
      const { tournamentId, finishPlace, prizeAmount } = action.payload || {}
      const id = String(tournamentId || '')
      if (!id) return
      const row = state.tournaments.find((t) => String(t.id) === id)
      if (row?.myRegistration) {
        row.myRegistration = {
          ...row.myRegistration,
          status: 'ELIMINATED',
          finishPlace: finishPlace ?? row.myRegistration.finishPlace,
          prizeAmount: prizeAmount ?? row.myRegistration.prizeAmount,
          playableTableId: null,
          canLeaveWithRefund: false,
          canLeaveForfeit: false,
          historyOnly: true
        }
      }
      state.registeredTournamentIds = state.registeredTournamentIds.filter((x) => x !== id)
      persistRegisteredIds(state.registeredTournamentIds)
    },
    setBlindLevel: (state, action) => {
      state.blindLevel = action.payload
    },
    setStandingsFromSocket: (state, action) => {
      const data = action.payload || {}
      if (Array.isArray(data.standings)) {
        state.standings = data.standings
      }
      state.standingsMeta = {
        tournamentId: data.tournamentId ?? state.standingsMeta?.tournamentId,
        tournamentTableId: data.tournamentTableId ?? state.standingsMeta?.tournamentTableId,
        prizePool: data.prizePool ?? state.standingsMeta?.prizePool,
        status: data.status ?? state.standingsMeta?.status
      }
    },
    clearSngGameState: (state) => {
      state.standings = []
      state.standingsMeta = null
      state.blindLevel = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSngLobbyThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSngLobbyThunk.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload || {}
        state.tournaments = Array.isArray(data.tournaments) ? data.tournaments : []

        // Hydrate registration from server — only active queue/seat rows
        const activeIds = []
        for (const t of state.tournaments) {
          const reg = t?.myRegistration
          if (reg?.id != null && !reg.historyOnly) {
            activeIds.push(String(t.id))
          }
        }
        const merged = new Set(activeIds)
        state.registeredTournamentIds = [...merged]
        persistRegisteredIds(state.registeredTournamentIds)
      })
      .addCase(getSngLobbyThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch SNG lobby'
      })
      .addCase(registerSngThunk.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(registerSngThunk.fulfilled, (state, action) => {
        state.actionLoading = false
        const tournamentId = String(action.payload?.tournamentId || '')
        if (tournamentId && !state.registeredTournamentIds.includes(tournamentId)) {
          state.registeredTournamentIds.push(tournamentId)
          persistRegisteredIds(state.registeredTournamentIds)
        }
        const count = action.payload?.registrationCount
        if (tournamentId && count != null) {
          const idx = state.tournaments.findIndex((t) => String(t.id) === tournamentId)
          if (idx !== -1) state.tournaments[idx].registrationCount = count
        }
        // Auto-start may remove the row via sngStarted / lobbyUpdate
        if (action.payload?.started && tournamentId) {
          state.tournaments = state.tournaments.filter((t) => String(t.id) !== tournamentId)
        }
      })
      .addCase(registerSngThunk.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to register'
      })
      .addCase(unregisterSngThunk.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(unregisterSngThunk.fulfilled, (state, action) => {
        state.actionLoading = false
        const tournamentId = String(action.payload?.tournamentId || '')
        state.registeredTournamentIds = state.registeredTournamentIds.filter((id) => id !== tournamentId)
        persistRegisteredIds(state.registeredTournamentIds)
        const count = action.payload?.registrationCount
        if (tournamentId && count != null) {
          const idx = state.tournaments.findIndex((t) => String(t.id) === tournamentId)
          if (idx !== -1) state.tournaments[idx].registrationCount = count
        }
      })
      .addCase(unregisterSngThunk.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload || 'Failed to unregister'
      })
      .addCase(getSngLeaderboardThunk.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.leaderboard = Array.isArray(data.leaderboard) ? data.leaderboard : []
        state.leaderboardCount = data.count ?? state.leaderboard.length
      })
      .addCase(getSngMyStatsThunk.fulfilled, (state, action) => {
        state.myStats = action.payload || null
      })
      .addCase(getSngStandingsThunk.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.standings = Array.isArray(data.standings) ? data.standings : []
        state.standingsMeta = {
          tournamentId: data.tournamentId,
          tournamentTableId: data.tournamentTableId,
          prizePool: data.prizePool,
          status: data.status
        }
      })
  }
})

export const {
  applySngLobbyUpdate,
  markRegistered,
  markUnregistered,
  markMyRegistrationEliminated,
  setBlindLevel,
  setStandingsFromSocket,
  clearSngGameState
} = sngSlice.actions

export default sngSlice.reducer
